import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Trash2, RefreshCw, User, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Settings = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [loadingFagerstrom, setLoadingFagerstrom] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const resetTrackingData = async () => {
    setLoadingTracking(true);
    try {
      const response = await axios.delete(`${API_URL}/tracking/reset`);
      toast.success(
        language === 'fr' 
          ? `${response.data.deleted_count} entrées de suivi supprimées` 
          : `${response.data.deleted_count} tracking entries deleted`
      );
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Error resetting data');
    } finally {
      setLoadingTracking(false);
    }
  };

  const resetFagerstromData = async () => {
    setLoadingFagerstrom(true);
    try {
      const response = await axios.delete(`${API_URL}/fagerstrom/reset`);
      toast.success(
        language === 'fr' 
          ? `${response.data.deleted_count} tests Fagerström supprimés` 
          : `${response.data.deleted_count} Fagerström tests deleted`
      );
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Error resetting data');
    } finally {
      setLoadingFagerstrom(false);
    }
  };

  const resetAllData = async () => {
    setLoadingAll(true);
    try {
      const response = await axios.delete(`${API_URL}/data/reset-all`);
      toast.success(
        language === 'fr' 
          ? 'Toutes les données ont été réinitialisées' 
          : 'All data has been reset'
      );
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Error resetting data');
    } finally {
      setLoadingAll(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="settings-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <Link to="/dashboard">
            <Button variant="ghost" className="rounded-full mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Retour' : 'Back'}
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {language === 'fr' ? 'Paramètres' : 'Settings'}
          </h1>
        </div>

        {/* User Info Card */}
        <Card className="glass-card rounded-3xl p-6 mb-6 animate-fade-in-up">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-gray-500">{user?.email}</p>
            </div>
          </div>
        </Card>

        {/* Reset Data Section */}
        <Card className="glass-card rounded-3xl p-6 mb-6 animate-fade-in-up animate-delay-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-primary" />
            {language === 'fr' ? 'Réinitialisation des données' : 'Data Reset'}
          </h3>
          <p className="text-gray-600 mb-6">
            {language === 'fr' 
              ? 'Réinitialisez vos données pour recommencer à zéro. Cette action est irréversible.'
              : 'Reset your data to start fresh. This action cannot be undone.'}
          </p>

          <div className="space-y-4">
            {/* Reset Weekly Tracking */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
              <div>
                <p className="font-semibold text-gray-900">
                  {language === 'fr' ? 'Suivi hebdomadaire' : 'Weekly tracking'}
                </p>
                <p className="text-sm text-gray-500">
                  {language === 'fr' ? 'Anxiété, envie, humeur, cigarettes' : 'Anxiety, craving, mood, cigarettes'}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="rounded-full border-orange-200 text-orange-600 hover:bg-orange-50"
                    data-testid="reset-tracking-btn"
                  >
                    {loadingTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {language === 'fr' ? 'Réinitialiser le suivi ?' : 'Reset tracking data?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {language === 'fr' 
                        ? 'Cette action supprimera toutes vos données de suivi hebdomadaire (anxiété, envie, humeur). Cette action est irréversible.'
                        : 'This will delete all your weekly tracking data (anxiety, craving, mood). This action cannot be undone.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={resetTrackingData}
                      className="rounded-full bg-orange-500 hover:bg-orange-600"
                    >
                      {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Reset Fagerström Tests */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
              <div>
                <p className="font-semibold text-gray-900">
                  {language === 'fr' ? 'Tests Fagerström' : 'Fagerström tests'}
                </p>
                <p className="text-sm text-gray-500">
                  {language === 'fr' ? 'Historique des tests de dépendance' : 'Dependency test history'}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="rounded-full border-orange-200 text-orange-600 hover:bg-orange-50"
                    data-testid="reset-fagerstrom-btn"
                  >
                    {loadingFagerstrom ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {language === 'fr' ? 'Réinitialiser les tests ?' : 'Reset Fagerström tests?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {language === 'fr' 
                        ? 'Cette action supprimera tous vos tests Fagerström et votre score de dépendance. Cette action est irréversible.'
                        : 'This will delete all your Fagerström tests and dependency score. This action cannot be undone.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={resetFagerstromData}
                      className="rounded-full bg-orange-500 hover:bg-orange-600"
                    >
                      {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Reset All Data */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl border border-red-100">
              <div>
                <p className="font-semibold text-red-900">
                  {language === 'fr' ? 'Toutes les données' : 'All data'}
                </p>
                <p className="text-sm text-red-600">
                  {language === 'fr' ? 'Supprimer toutes vos données' : 'Delete all your data'}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-100"
                    data-testid="reset-all-btn"
                  >
                    {loadingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    {language === 'fr' ? 'Tout supprimer' : 'Delete all'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-3xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-red-600">
                      {language === 'fr' ? 'Supprimer toutes les données ?' : 'Delete all data?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {language === 'fr' 
                        ? 'Cette action supprimera TOUTES vos données : suivi hebdomadaire et tests Fagerström. Vous repartirez de zéro. Cette action est IRRÉVERSIBLE.'
                        : 'This will delete ALL your data: weekly tracking and Fagerström tests. You will start from scratch. This action is IRREVERSIBLE.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">
                      {language === 'fr' ? 'Annuler' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={resetAllData}
                      className="rounded-full bg-red-500 hover:bg-red-600"
                    >
                      {language === 'fr' ? 'Tout supprimer' : 'Delete all'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </Card>

        {/* Logout */}
        <Card className="glass-card rounded-3xl p-6 animate-fade-in-up animate-delay-200">
          <Button 
            onClick={() => { logout(); navigate('/'); }}
            variant="outline"
            className="w-full rounded-full border-gray-200 text-gray-600 hover:bg-gray-50"
            data-testid="logout-settings-btn"
          >
            {language === 'fr' ? 'Se déconnecter' : 'Log out'}
          </Button>
        </Card>
      </div>
    </div>
  );
};
